import React, { useEffect, useState, useContext, useRef } from 'react';
import {
  Grid,
  Typography,
  Box,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { AppContext } from '../../context/Context';
import { useTranslation } from 'react-i18next';
import { SocketContext } from '../../context/SocketContext';

import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import '../../assets/css/buttons.css';
import {
  PointOfSale as PointOfSaleIcon,
  AddCard as AddCardIcon,
  ExitToApp as ExitToAppIcon,
  CalendarToday as CalendarTodayIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import WGLogo from '../../assets/img/wingames.png';
import CHLogo from '../../assets/img/ClubHouse_Logo_Light.png';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import CashField from './CashField';
import { formatSignedCash } from '../../utils/formatCash';
import {
  centeredLabelSx,
  verticallyCenteredLabelSx,
  fieldHelpSx,
} from '../../theme/fieldTokens';

const draftKey = (hall) => `endShiftCount:${hall}`;

// Count step: fixed so the panel keeps one shape when tabs change section
const TABLE_HEIGHT = '56vh';

// Confirm step stacks table over summary, so 56vh overflowed the modal
const CONFIRM_TABLE_HEIGHT = '34vh';

// Past this the draft is from an earlier shift, not an interrupted count
const DRAFT_MAX_AGE_MS = 6 * 60 * 60 * 1000;

const readDraft = (hall) => {
  try {
    const raw = window.localStorage.getItem(draftKey(hall));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeDraft = (hall, draft) => {
  try {
    window.localStorage.setItem(draftKey(hall), JSON.stringify(draft));
  } catch {
    // A full or blocked storage must not stop a shift from closing
  }
};

const clearDraft = (hall) => {
  try {
    window.localStorage.removeItem(draftKey(hall));
  } catch {
    // as above
  }
};

function ModalEndShiftAuto() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    hall,
    operator,
    session,
    updateShowModalEndShift,
    updateIsDailyReportCompleted,
    updateIsCardReportCompleted,
    updateIsLoadingScreen,
    isLoadingScreen,
    printer,
    modifyLogin,
    modifyOperator,
    modifyOperatorFull,
    modifyHall,
    updateSession,
  } = useContext(AppContext);

  const hallRef = useRef(hall);

  useEffect(() => {
    hallRef.current = hall;
    socket.emit('checkShiftAndDayNumbers', {
      action: 'checkShiftAndDayNumbers',
      bingoName: hallRef.current,
      printer: printerRef.current,
    });
  }, [hall]);

  const isLoadingScreenRef = useRef(isLoadingScreen);
  useEffect(() => {
    isLoadingScreenRef.current = isLoadingScreen;
  }, [isLoadingScreen]);

  const operatorRef = useRef(operator);
  useEffect(() => {
    operatorRef.current = operator;
  }, [operator]);

  const sessionRef = useRef(session);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const printerRef = useRef(printer);
  useEffect(() => {
    printerRef.current = printer;
  }, [printer]);

  const { socket } = useContext(SocketContext);

  const [shiftNumbers, setShiftNumbers] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  // 0 Kasse · 1 Selvbetjening · 2 Totalt — see SECTIONS
  const [activeSection, setActiveSection] = useState(0);

  // Steps: 'choice' → 'count' (count, explain, sign) → 'confirm'; expected stays hidden until counted
  const [step, setStep] = useState('choice');
  // 'shift' → close shift only; 'day' → close shift + last of the day
  const [closeType, setCloseType] = useState(null);

  const [countedCash, setCountedCash] = useState('');
  const [avvikRevealed, setAvvikRevealed] = useState(false);
  const [avvikComment, setAvvikComment] = useState('');
  const [ntNetto, setNtNetto] = useState('');
  const [rikstotoNetto, setRikstotoNetto] = useState('');
  const [signedBy, setSignedBy] = useState('');
  const [signedByID, setSignedByID] = useState('');
  // Empty until the list arrives; until then the free-text field is what shows
  const [operators, setOperators] = useState([]);
  const [signConfirmed, setSignConfirmed] = useState(false);

  const countedNumber = countedCash === '' ? null : Number(countedCash);
  const ntNumber = ntNetto === '' ? null : Number(ntNetto);
  const rikstotoNumber = rikstotoNetto === '' ? null : Number(rikstotoNetto);

  // NT and Rikstoto run other software but the same drawer, so their net counts
  const externalNetto = (ntNumber ?? 0) + (rikstotoNumber ?? 0);
  const cashExpected =
    shiftNumbers?.cash?.expected == null
      ? null
      : shiftNumbers.cash.expected + externalNetto;

  const avvik =
    countedNumber != null && cashExpected != null
      ? countedNumber - cashExpected
      : null;

  // Mandatory every close: a blank is not a zero, it is an unanswered question
  const countInputsComplete =
    countedCash !== '' && ntNetto !== '' && rikstotoNetto !== '';

  // The employees offered in the signature select
  useEffect(() => {
    if (!socket || !hall) return;

    const handleOperators = (data) => {
      setOperators(Array.isArray(data?.operators) ? data.operators : []);
      if (data?.ok === false) {
        console.warn('listOperators unavailable:', data.reason);
      }
    };

    socket.on('listOperators', handleOperators);
    socket.emit('listOperators', {});
    return () => socket.off('listOperators', handleOperators);
  }, [socket, hall]);

  // Restore an unfinished count — values only, never the step
  const draftLoadedFor = useRef(null);
  useEffect(() => {
    if (!hall || draftLoadedFor.current === hall) return;
    draftLoadedFor.current = hall;

    const draft = readDraft(hall);
    if (!draft) return;

    // A draft older than a shift belongs to a count nobody is still doing
    if (!draft.savedAt || Date.now() - draft.savedAt > DRAFT_MAX_AGE_MS) {
      clearDraft(hall);
      return;
    }

    setCountedCash(draft.countedCash ?? '');
    setAvvikComment(draft.avvikComment ?? '');
    setNtNetto(draft.ntNetto ?? '');
    setRikstotoNetto(draft.rikstotoNetto ?? '');
    setSignedBy(draft.signedBy ?? '');
    setSignedByID(draft.signedByID ?? '');

    if (draft.countedCash) toast(t('endShift.draftRestored'));
  }, [hall]);

  useEffect(() => {
    if (!hall || draftLoadedFor.current !== hall) return;
    if (!countedCash && !avvikComment && !signedBy && !signedByID) return;

    writeDraft(hall, {
      savedAt: Date.now(),
      countedCash,
      avvikComment,
      ntNetto,
      rikstotoNetto,
      signedBy,
      signedByID,
    });
  }, [
    hall,
    countedCash,
    avvikComment,
    ntNetto,
    rikstotoNetto,
    signedBy,
    signedByID,
  ]);

  useEffect(() => {
    const handleCloseShift = (data) => {
      updateIsLoadingScreen(false);

      if (!data) {
        toast.error(t('error.error'));
        return;
      }

      // The server recomputes expected from the rows, so a close can come back refused
      if (data.ok === false) {
        if (data.reason === 'avvikCommentRequired') {
          toast.error(t('endShift.avvikCommentRequired'));
          setStep('count');
          setAvvikRevealed(true);
        } else if (data.reason === 'externalNettoRequired') {
          toast.error(t('endShift.externalNettoRequired'));
          setStep('count');
          setAvvikRevealed(false);
        } else if (data.reason === 'noOpenShift') {
          toast.error(t('endShift.noOpenShift'));
        } else {
          toast.error(t('error.error'));
        }
        return;
      }

      clearDraft(hallRef.current);
      modifyLogin(false);
      modifyOperator('');
      modifyOperatorFull('');
      modifyHall('');
      updateSession('');
      updateShowModalEndShift(false);
      navigate('/login');
    };

    const handleCheckShiftAndDayNumbers = (data) => {
      setShiftNumbers(data);
      updateIsLoadingScreen(false);
    };

    socket.on('closeShift', handleCloseShift);
    socket.on('checkShiftAndDayNumbers', handleCheckShiftAndDayNumbers);

    return () => {
      socket.off('closeShift', handleCloseShift);
      socket.off('checkShiftAndDayNumbers', handleCheckShiftAndDayNumbers);
    };
  }, [socket]);

  // Backing out abandons the count: leaving the draft would prefill the next one
  const handleCancel = () => {
    clearDraft(hallRef.current);
    updateShowModalEndShift(false);
  };

  const handleChoose = (type) => {
    setCloseType(type);
    setStep('count');
  };

  const handleBack = () => {
    if (step === 'confirm') {
      setStep('count');
      return;
    }
    setStep('choice');
    setCloseType(null);
  };

  // Reveal is one-way: "Tell på nytt" clears the figure and hides it again
  const handleReveal = () => {
    if (countedCash === '') {
      toast.error(t('endShift.countMissing'));
      return;
    }
    if (ntNetto === '' || rikstotoNetto === '') {
      toast.error(t('endShift.externalNettoRequired'));
      return;
    }
    setAvvikRevealed(true);
  };

  // Clears the external nets too: they feed expected, so keeping them is not a fresh start
  const handleRecount = () => {
    setCountedCash('');
    setNtNetto('');
    setRikstotoNetto('');
    setAvvikRevealed(false);
  };

  const handleCountDone = () => {
    if (!countInputsComplete) {
      toast.error(t('endShift.countMissing'));
      return;
    }
    if (!avvikRevealed) {
      handleReveal();
      return;
    }
    if (avvik !== null && avvik !== 0 && !avvikComment.trim()) {
      toast.error(t('endShift.avvikCommentRequired'));
      return;
    }
    // With employees on file the id is the signature; without, the old text field is
    const signValid = operators.length ? signedByID !== '' : signedBy.trim();
    if (!signValid || !signConfirmed) {
      toast.error(t('endShift.signRequired'));
      return;
    }
    setStep('confirm');
  };

  const handleFinishConfirm = () => {
    updateIsLoadingScreen(true);
    socket.emit('closeShift', {
      action: 'closeShift',
      bingoName: hallRef.current,
      operator: operatorRef.current,
      session: sessionRef.current,
      sessionID: sessionRef.current,
      printer: printerRef.current,
      isLastOfTheDay: closeType === 'day',
      cashCounted: countedNumber,
      avvikComment: avvikComment.trim() || null,
      signedBy: signedBy.trim() || null,
      signedByID: signedByID === '' ? null : Number(signedByID),
      ntNetto: ntNetto === '' ? null : Number(ntNetto),
      rikstotoNetto: rikstotoNetto === '' ? null : Number(rikstotoNetto),
    });
  };

  const fmt = (v) =>
    v !== undefined && v !== null ? Number(v).toFixed(2) : '0.00';

  // Only the type scale is set here; colours and zebra come from the dark modal skin
  const tableStyles = (compact) => ({
    '& .MuiTableCell-root': {
      fontSize: compact ? '15px' : '19px',
      paddingTop: compact ? '5px' : '10px',
      paddingBottom: compact ? '5px' : '10px',
      ...(compact && { paddingLeft: '10px', paddingRight: '10px' }),
      whiteSpace: 'nowrap',
    },
    '& .MuiTableCell-head': { fontSize: compact ? '13px' : '17px' },
  });

  const cellSx = {};

  // Rows the operator can add up to reach Forventet; the informational ones sit below the total
  const renderProviderRows = (section, isKiosk = false, isDrawer = false) => {
    // CH and CH Spillerkonto are two halves of one provider, so every section shows their sum
    const chTotalData = {
      cashIn:
        (section?.ch?.cashIn ?? 0) + (section?.chSpillerkonto?.cashIn ?? 0),
      cashOut:
        (section?.ch?.cashOut ?? 0) + (section?.chSpillerkonto?.cashOut ?? 0),
      neto: (section?.ch?.neto ?? 0) + (section?.chSpillerkonto?.neto ?? 0),
    };

    // Own till, other software: the legacy rows plus the netto typed in at close
    const ntData = {
      neto:
        (section?.nt?.cashIn ?? 0) -
        (section?.nt?.cashOut ?? 0) +
        (ntNumber ?? 0),
    };

    const providers = [
      { key: 'CH', data: section?.ch },
      { key: 'CH Spillerkonto', data: section?.chSpillerkonto },
      { key: 'OK', data: section?.ok },
      { key: 'WG', data: section?.wg },
      ...(!isKiosk ? [{ key: 'Kiosk', data: section?.kiosk }] : []),
      ...(!isKiosk ? [{ key: 'NT', data: ntData, external: true }] : []),
      ...(!isKiosk
        ? [
            {
              key: 'Rikstoto',
              data: { neto: rikstotoNumber ?? 0 },
              external: true,
            },
          ]
        : []),
    ];

    // Sales settled by card never reached the drawer, so the figure gives them back
    const deductions = isKiosk
      ? []
      : [
          {
            key: t('endShift.paymentCard'),
            cashOut: section?.payments?.card,
            neto: -(section?.payments?.card ?? 0),
          },
          {
            key: t('endShift.refunds'),
            cashOut: section?.refunds,
            neto: -(section?.refunds ?? 0),
          },
          {
            key: t('endShift.cashWithdrawalRow'),
            cashIn: section?.cashWithdrawal?.cashIn,
            cashOut: section?.cashWithdrawal?.cashOut,
            neto: section?.cashWithdrawal?.neto ?? 0,
          },
          {
            key: t('endShift.tillMovements'),
            cashIn: section?.till?.cashIn,
            cashOut: section?.till?.cashOut,
            neto: section?.till?.neto ?? 0,
          },
        ];

    // Only the shift's Kasse section is the drawer; the day and the totals are not counted against it
    const opening = shiftNumbers?.cash?.opening;
    const showTotals = isDrawer && cashExpected != null && opening != null;

    const line = (label, value, { bold, dim } = {}) => (
      <TableRow key={label}>
        <TableCell
          colSpan={3}
          sx={{
            ...cellSx,
            ...(bold && { fontWeight: 'bold' }),
            ...(dim && { color: '#8fa3d4' }),
          }}
        >
          {label}
        </TableCell>
        <TableCell
          align="right"
          sx={{
            ...cellSx,
            fontWeight: 'bold',
            ...(dim && { color: '#8fa3d4' }),
          }}
        >
          {fmt(value)}
        </TableCell>
      </TableRow>
    );

    const movementRow = ({ key, data, cashIn, cashOut, neto, external }) => (
      <TableRow key={key}>
        <TableCell sx={cellSx}>{key}</TableCell>
        <TableCell align="right" sx={cellSx}>
          {external ? '-' : fmt(data ? data.cashIn : cashIn)}
        </TableCell>
        <TableCell align="right" sx={cellSx}>
          {external ? '-' : fmt(data ? data.cashOut : cashOut)}
        </TableCell>
        <TableCell align="right" sx={{ ...cellSx, fontWeight: 'bold' }}>
          {fmt(data ? data.neto : neto)}
        </TableCell>
      </TableRow>
    );

    return (
      <>
        {showTotals && line(t('endShift.openingCashRow'), opening)}
        {providers.map(movementRow)}
        {deductions.map(movementRow)}
        {showTotals &&
          line(t('endShift.movementsSum'), cashExpected - opening, {
            bold: true,
          })}
        {showTotals &&
          line(t('endShift.expectedInTill'), cashExpected, { bold: true })}

        {/* Below the total on purpose: both double-count rows already above them */}
        <TableRow key="informational">
          <TableCell colSpan={4} sx={{ ...cellSx, color: '#8fa3d4' }}>
            {t('endShift.informational')}
          </TableCell>
        </TableRow>
        {movementRow({
          key: 'CH Total',
          data: chTotalData,
        })}
        {!isKiosk &&
          movementRow({
            key: t('endShift.paymentCash'),
            cashIn: section?.payments?.cashIn,
            cashOut: section?.payments?.cashOut,
            neto:
              (section?.payments?.cashIn ?? 0) -
              (section?.payments?.cashOut ?? 0),
          })}
      </>
    );
  };

  const renderTableSection = (
    sectionData,
    label,
    isKiosk = false,
    height = TABLE_HEIGHT,
    compact = false,
    isDrawer = false
  ) => (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {label && (
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 'bold',
            mb: 0.5,
            color: 'white',
            textAlign: 'center',
          }}
        >
          {label}
        </Typography>
      )}
      {/* overflowX so a narrow panel scrolls the table, not the modal */}
      <TableContainer sx={{ height, overflowY: 'auto', overflowX: 'auto' }}>
        <Table stickyHeader sx={tableStyles(compact)}>
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              <TableCell align="right">{t('endShift.cashin')}</TableCell>
              <TableCell align="right">{t('endShift.cashout')}</TableCell>
              <TableCell align="right">{t('endShift.neto')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {renderProviderRows(sectionData, isKiosk, isDrawer)}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const periodData =
    activeTab === 0 ? shiftNumbers?.currentShift : shiftNumbers?.day;

  // Which section is on screen; Kasse by default, it is what the drawer figure is built from
  const SECTIONS = [
    { label: t('endShift.cashierSection'), key: 'cashier', isKiosk: false },
    { label: t('endShift.selfKioskSection'), key: 'selfKiosk', isKiosk: true },
    { label: t('endShift.totalSection'), key: 'total', isKiosk: false },
  ];

  const tabSx = {
    borderBottom: '1px solid #2a3a6e',
    minHeight: '56px',
    '& .MuiTab-root': {
      color: '#8fa3d4',
      // Touch panel: the MUI default is a 12px label on a 48px row
      fontSize: '17px',
      minHeight: '56px',
    },
    '& .MuiTab-root.Mui-selected': { color: 'white', fontWeight: 'bold' },
    '& .MuiTabs-indicator': { backgroundColor: '#fcad89' },
  };

  // Confirm step: two tab rows at 56px each ate the height the table needed
  const compactTabSx = {
    ...tabSx,
    minHeight: '42px',
    '& .MuiTab-root': {
      ...tabSx['& .MuiTab-root'],
      fontSize: '14px',
      minHeight: '42px',
    },
  };

  // Provider movements, shared by the count and confirm steps; shown only after the reveal
  const renderMovementsPanel = (compact = false) => {
    const section = SECTIONS[activeSection] ?? SECTIONS[0];
    const panelTabSx = compact ? compactTabSx : tabSx;

    return (
      <Paper
        elevation={2}
        sx={{
          mb: compact ? 1.5 : 2,
          overflow: 'hidden',
          backgroundColor: '#232a45',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ ...panelTabSx, backgroundColor: '#151d3b' }}
        >
          <Tab label={t('endShift.currentShift')} />
          <Tab label={t('endShift.day')} />
        </Tabs>
        <Tabs
          value={activeSection}
          onChange={(_, v) => setActiveSection(v)}
          variant="fullWidth"
          sx={{ ...panelTabSx, backgroundColor: '#1a2344' }}
        >
          {SECTIONS.map((s) => (
            <Tab key={s.key} label={s.label} />
          ))}
        </Tabs>
        <Box sx={{ p: compact ? 1 : 1.5 }}>
          {renderTableSection(
            periodData?.[section.key],
            null, // the tab already names it
            section.isKiosk,
            compact ? CONFIRM_TABLE_HEIGHT : TABLE_HEIGHT,
            compact,
            // The drawer is the shift's Kasse: the day carries another opening balance
            section.key === 'cashier' && activeTab === 0
          )}
        </Box>
      </Paper>
    );
  };

  return (
    <>
      {isLoadingScreen ? (
        <></>
      ) : (
        <div id="react-confirm-alert">
          <div className="react-confirm-alert-overlay">
            <div className="react-confirm-alert">
              {/* ── STEP 1: CHOICE ── */}
              {step === 'choice' && (
                <div
                  className="react-confirm-alert-body"
                  style={{ width: '700px' }}
                >
                  <h1>{t('endShift.choiceTitle')}</h1>
                  <p>{t('endShift.choiceSubtitle')}</p>
                  {/* btn-choice, not btn-custom-*: the latter carry
                      `width: 200px !important`, which beat the inline width and
                      left these three laid out at random. Same grid as
                      ModalCashMovement, so both choice screens read alike. */}
                  <div className="choice-grid">
                    <button
                      className="btn-choice tone-in"
                      onClick={() => handleChoose('shift')}
                    >
                      <ExitToAppIcon />
                      {t('endShift.closeShift')}
                    </button>
                    <button
                      className="btn-choice tone-out"
                      onClick={() => handleChoose('day')}
                    >
                      <CalendarTodayIcon />
                      {t('endShift.closeDay')}
                    </button>
                    <button
                      className="btn-choice tone-dismiss choice-wide"
                      onClick={handleCancel}
                    >
                      <CancelIcon />
                      {t('sales.cancel')}
                    </button>
                  </div>
                </div>
              )}

              {/* ── STEP 2: COUNT THE DRAWER ── */}
              {step === 'count' && (
                <div
                  className="react-confirm-alert-body modal-status"
                  // Narrow while counting, full panel once the tables are up
                  style={
                    avvikRevealed
                      ? {
                          // as in the confirm step: padding sits outside width
                          boxSizing: 'border-box',
                          width: '96vw',
                          maxWidth: '1900px',
                          // Safety net on a short panel: the modal blocks and has no Escape
                          maxHeight: '94vh',
                          overflowY: 'auto',
                        }
                      : { width: '820px' }
                  }
                >
                  <h1>{t('endShift.countTitle')}</h1>
                  <p>
                    {avvikRevealed
                      ? t('endShift.countSubtitle')
                      : t('endShift.countedHelp')}
                  </p>

                  {/* All three come from counting and reading, never from what
                      the till expects — so they are all committed before the
                      reveal. NT and Rikstoto feed the expected figure, so an
                      avvik cannot be worked out until they are in. */}
                  {!avvikRevealed && (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          marginTop: '20px',
                        }}
                      >
                        <CashField
                          label={t('endShift.countedLabel') + '*'}
                          value={countedCash}
                          onChange={setCountedCash}
                          autoFocus
                        />
                      </div>

                      <Typography
                        sx={{
                          color: '#8fa3d4',
                          fontSize: '15px',
                          mt: 5,
                          mb: 1,
                          textAlign: 'center',
                        }}
                      >
                        {t('endShift.externalTillHelp')}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 2,
                          justifyContent: 'center',
                          mt: 3,
                        }}
                      >
                        <CashField
                          label={t('endShift.ntNetto') + '*'}
                          value={ntNetto}
                          onChange={setNtNetto}
                          width="240px"
                          fontSize="20px"
                        />
                        <CashField
                          label={t('endShift.rikstotoNetto') + '*'}
                          value={rikstotoNetto}
                          onChange={setRikstotoNetto}
                          width="240px"
                          fontSize="20px"
                        />
                      </Box>
                    </>
                  )}

                  {!avvikRevealed && (
                    <div
                      className="react-confirm-alert-button-group"
                      style={{ marginTop: '32px' }}
                    >
                      <button
                        className="btn-custom-yes"
                        onClick={handleReveal}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                        }}
                      >
                        <CheckCircleIcon style={{ fontSize: '1.2em' }} />
                        {t('endShift.showAvvik')}
                      </button>
                      <button
                        className="btn-custom-back"
                        onClick={handleBack}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                        }}
                      >
                        <ArrowBackIcon style={{ fontSize: '1.2em' }} />
                        {t('endShift.back')}
                      </button>
                    </div>
                  )}

                  {avvikRevealed && (
                    <>
                      {/* Left: what is being signed. Right: where the money
                          went. Side by side because the comment cannot be
                          written without reading the movements, and making the
                          operator flip between two screens to do it is how
                          "annet avvik" ends up in every comment field. */}
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 3,
                          alignItems: 'flex-start',
                          mt: 3,
                          textAlign: 'left',
                        }}
                      >
                        <Box sx={{ width: '520px', flexShrink: 0 }}>
                          <Box
                            sx={{
                              mb: 2,
                              px: 3,
                              py: 2,
                              borderRadius: '14px',
                              backgroundColor: '#151d3b',
                              border: '1px solid #2a3a6e',
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                py: 0.5,
                                color: '#e6eaf2',
                                fontSize: '20px',
                                fontWeight: 600,
                              }}
                            >
                              <span>{t('endShift.countedLabel')}</span>
                              <span>{formatSignedCash(countedNumber)} kr</span>
                            </Box>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                py: 0.5,
                                color: '#8fa3d4',
                                fontSize: '20px',
                              }}
                            >
                              <span>{t('endShift.expected')}</span>
                              <span>
                                {cashExpected == null
                                  ? t('endShift.expectedUnknown')
                                  : `${formatSignedCash(cashExpected)} kr`}
                              </span>
                            </Box>

                            {/* Shown as their own lines so the operator can see
                                the two figures they typed inside the expected
                                total, rather than wondering where it moved */}
                            {[
                              [t('endShift.ntNetto'), ntNumber],
                              [t('endShift.rikstotoNetto'), rikstotoNumber],
                            ].map(([label, value]) => (
                              <Box
                                key={label}
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  py: 0.25,
                                  pl: 2,
                                  color: '#6e82b4',
                                  fontSize: '16px',
                                }}
                              >
                                <span>{label}</span>
                                <span>{formatSignedCash(value ?? 0)} kr</span>
                              </Box>
                            ))}
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                pt: 1,
                                mt: 1,
                                borderTop: '1px solid #2a3a6e',
                                fontSize: '30px',
                                fontWeight: 'bold',
                                // Green only when it balances; an avvik of any size must not look like a pass
                                color:
                                  avvik === null
                                    ? '#8fa3d4'
                                    : avvik === 0
                                      ? '#5ad19b'
                                      : '#fcad89',
                              }}
                            >
                              <span>{t('endShift.avvik')}</span>
                              <span>
                                {avvik === null
                                  ? '—'
                                  : `${avvik > 0 ? '+' : ''}${formatSignedCash(
                                      avvik
                                    )} kr`}
                              </span>
                            </Box>
                            {avvik !== null && (
                              <Box
                                sx={{
                                  mt: 0.5,
                                  textAlign: 'right',
                                  color: '#8fa3d4',
                                  fontSize: '15px',
                                }}
                              >
                                {avvik === 0
                                  ? t('endShift.avvikNone')
                                  : avvik > 0
                                    ? t('endShift.avvikOver')
                                    : t('endShift.avvikUnder')}
                              </Box>
                            )}
                          </Box>

                          {avvik !== null && avvik !== 0 && (
                            <>
                              <TextField
                                autoComplete="off"
                                label={t('endShift.avvikCommentLabel') + '*'}
                                value={avvikComment}
                                onChange={(e) =>
                                  setAvvikComment(e.target.value)
                                }
                                variant="outlined"
                                multiline
                                maxRows={4}
                                inputProps={{ maxLength: 500 }}
                                InputLabelProps={{
                                  sx: verticallyCenteredLabelSx,
                                }}
                                className="modal-textfield"
                                style={{ width: '100%' }}
                              />
                              <Typography sx={fieldHelpSx}>
                                {t('endShift.avvikCommentHelp')}
                              </Typography>
                            </>
                          )}

                          {operators.length === 0 ? (
                            // The free-text field is the default and the fallback: an
                            // empty Select still opens a focus-trapping menu with
                            // nothing in it, which locks every other input on screen
                            <TextField
                              autoComplete="off"
                              label={t('endShift.signedByLabel') + '*'}
                              value={signedBy}
                              onChange={(e) => setSignedBy(e.target.value)}
                              variant="outlined"
                              inputProps={{
                                maxLength: 100,
                                style: { textAlign: 'center' },
                              }}
                              InputLabelProps={{ sx: centeredLabelSx }}
                              className="modal-textfield"
                              style={{ width: '340px' }}
                            />
                          ) : (
                            <TextField
                              select
                              label={t('endShift.signedByLabel') + '*'}
                              value={signedByID}
                              onChange={(e) => {
                                setSignedByID(e.target.value);
                                const picked = operators.find(
                                  (o) => o.id === Number(e.target.value)
                                );
                                setSignedBy(picked ? picked.name : '');
                              }}
                              variant="outlined"
                              InputLabelProps={{ sx: centeredLabelSx }}
                              className="modal-textfield"
                              style={{ width: '340px' }}
                              SelectProps={{
                                // The menu renders in a portal, so the modal skin
                                // cannot reach it — it carries its own colours
                                MenuProps: {
                                  // #react-confirm-alert is z-index 999999, the MUI
                                  // portal defaults to 1300: without this the menu
                                  // opens *behind* the modal, invisible but still
                                  // trapping focus, which locks every other field
                                  sx: { zIndex: 1000000 },
                                  PaperProps: {
                                    sx: {
                                      backgroundColor: '#151d3b',
                                      color: '#c9d6f2',
                                      maxHeight: 360,
                                      '& .MuiMenuItem-root': {
                                        fontSize: '20px',
                                        // Touch panel: 44px is below a reliable target
                                        minHeight: 56,
                                        justifyContent: 'center',
                                      },
                                      '& .MuiMenuItem-root.Mui-selected': {
                                        backgroundColor: '#28407a',
                                      },
                                    },
                                  },
                                },
                              }}
                              inputProps={{ style: { textAlign: 'center' } }}
                            >
                              {operators.map((o) => (
                                <MenuItem key={o.id} value={o.id}>
                                  {o.name}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}
                          <Typography sx={fieldHelpSx}>
                            {t('endShift.signedByHelp')}
                          </Typography>

                          <FormControlLabel
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              color: '#c9d6f2',
                              mb: 1,
                            }}
                            control={
                              <Checkbox
                                checked={signConfirmed}
                                onChange={(e) =>
                                  setSignConfirmed(e.target.checked)
                                }
                                sx={{
                                  color: '#8fa3d4',
                                  '&.Mui-checked': { color: '#5ad19b' },
                                  // Touch panel: the default box is a 20px target
                                  '& .MuiSvgIcon-root': { fontSize: 30 },
                                }}
                              />
                            }
                            label={t('endShift.signConfirm')}
                          />
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {renderMovementsPanel()}
                        </Box>
                      </Box>

                      <div className="react-confirm-alert-button-group">
                        <button
                          className="btn-custom-yes"
                          onClick={handleCountDone}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                          }}
                        >
                          <CheckCircleIcon style={{ fontSize: '1.2em' }} />
                          {t('sales.confirm')}
                        </button>
                        <button
                          className="btn-custom-postpone"
                          onClick={handleRecount}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                          }}
                        >
                          <ArrowBackIcon style={{ fontSize: '1.2em' }} />
                          {t('endShift.recount')}
                        </button>
                        <button
                          className="btn-custom-no"
                          onClick={handleCancel}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                          }}
                        >
                          <CancelIcon style={{ fontSize: '1.2em' }} />
                          {t('sales.cancel')}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ── STEP 3: CONFIRM ── */}
              {step === 'confirm' && (
                <div
                  className="react-confirm-alert-body"
                  style={{
                    // Library padding sits outside width, so 96vw overflowed
                    boxSizing: 'border-box',
                    width: '92vw',
                    maxWidth: '1400px',
                    maxHeight: '94vh',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    padding: '20px 24px',
                  }}
                >
                  <h1 style={{ fontSize: '24px' }}>{t('endShift.title')}</h1>
                  <p style={{ fontSize: '16px', marginBottom: '10px' }}>
                    {t('endShift.subtitle')}
                  </p>
                  {renderMovementsPanel(true)}

                  {/* What is about to be signed, restated next to the numbers
                      it will be filed with. Last chance to go back. */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 2.5,
                      justifyContent: 'center',
                      alignItems: 'baseline',
                      mb: 1.5,
                      px: 2,
                      py: 1,
                      borderRadius: '14px',
                      backgroundColor: '#151d3b',
                      border: '1px solid #2a3a6e',
                      color: '#c9d6f2',
                      fontSize: '16px',
                    }}
                  >
                    <span>
                      {t('endShift.countedLabel')}:{' '}
                      <strong>{formatSignedCash(countedNumber)} kr</strong>
                    </span>
                    <span>
                      {t('endShift.expected')}:{' '}
                      <strong>
                        {cashExpected == null
                          ? t('endShift.expectedUnknown')
                          : `${formatSignedCash(cashExpected)} kr`}
                      </strong>
                    </span>
                    <span
                      style={{
                        color:
                          avvik === null
                            ? '#8fa3d4'
                            : avvik === 0
                              ? '#5ad19b'
                              : '#fcad89',
                      }}
                    >
                      {t('endShift.avvik')}:{' '}
                      <strong>
                        {avvik === null
                          ? '—'
                          : `${avvik > 0 ? '+' : ''}${formatSignedCash(
                              avvik
                            )} kr`}
                      </strong>
                    </span>
                    <span>
                      {t('endShift.signedByLabel')}: <strong>{signedBy}</strong>
                    </span>
                  </Box>

                  <div className="react-confirm-alert-button-group">
                    <button
                      className="btn-custom-yes"
                      onClick={handleFinishConfirm}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                      }}
                    >
                      <CheckCircleIcon style={{ fontSize: '1.2em' }} />
                      {t('sales.confirm')}
                    </button>
                    <button
                      className="btn-custom-back"
                      onClick={handleBack}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                      }}
                    >
                      <ArrowBackIcon style={{ fontSize: '1.2em' }} />
                      {t('endShift.back')}
                    </button>
                    <button
                      className="btn-custom-no"
                      onClick={handleCancel}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                      }}
                    >
                      <CancelIcon style={{ fontSize: '1.2em' }} />
                      {t('sales.cancel')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ModalEndShiftAuto;
